'use client';

// Shows the plans screen once per browser session, wherever the visitor
// lands first (landing, editor, a shared room) — mounted once at the root
// layout, inside UpgradeModalProvider, so it reaches every route without
// living inside the provider itself.
//
// It is deliberately its OWN component rather than an effect inside
// UpgradeModalProvider: that provider is also the bare test harness roughly
// two dozen unrelated unit tests reach for just to satisfy `useUpgradeModal`
// — an unconditional auto-open effect living there forced the real
// UpgradeModal (and its useRouter() call) to mount inside every one of them,
// which crashes any test that never expected to render it. Splitting this
// out means only whoever actually renders <SessionPricingGate/> (production
// layout.tsx, and this file's own tests) is affected.
//
// Deleted 2026-09-09 as "measured dead" and restored 2026-09-12: the
// attribution said otherwise. The last three purchases before the deletion
// (3–4 Sep, all semiannual) came through this gate's 'session_start'
// trigger — three of the last five tracked purchases in the product's
// history. The 3 days after it went: 18 plan-screen opens, 2 selections,
// 0 checkouts.
//
// TWO people never see it, and both exclusions were learned the hard way by
// opening an ad link in a private window:
//
//   1. A FIRST-TIME visitor. Pricing before the product is a pitch to
//      someone who has not been shown anything, and the restored gate fired
//      on the first paint of a first-ever visit. It now waits for a return
//      visit — the visitor has to have closed the site and come back — which
//      is also the population the attribution above was actually measuring,
//      since those buyers were people who already knew what this was.
//   2. Anyone who arrived through a paid ad THIS SESSION. You paid for that
//      click; opening on a paywall before they have seen a single thing is
//      the worst possible use of it. They still meet the post-onboarding
//      paywall once they have a result, which is the moment that converts.

import { useEffect, useRef } from 'react';
import { hasUnlimitedCompute } from '@roomtreatment/core';
import { isContentPath } from '@roomtreatment/seo-content';
import { useNativeHost } from '@/components/room/state/NativeHostContext';
import { useUpgradeModal } from './UpgradeModalProvider';
import { useWallet } from '@/lib/useWallet';
import { storedCampaignAngle } from '@/lib/campaign/resolveAngle';

/** sessionStorage (not localStorage): cleared when the tab/window closes,
 *  kept across reloads and in-app navigation — exactly "closed the site and
 *  opened it again" vs. "refreshed the page". */
const SESSION_PRICING_KEY = 'roomtreatment.session_pricing_shown';

/** `null` when storage is unreachable (private mode, storage disabled) — the
 *  gate treats that as "can't remember, don't prompt" rather than risking the
 *  plans screen on every single navigation. */
function readSessionShown(): boolean | null {
  try {
    return sessionStorage.getItem(SESSION_PRICING_KEY) === '1';
  } catch {
    return null;
  }
}

/** Returns false when the write could not be made, for the same reason. */
function markSessionShown(): boolean {
  try {
    sessionStorage.setItem(SESSION_PRICING_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/** localStorage, deliberately — this one has to outlive the tab. It is the
 *  difference between "refreshed" and "came back another day". */
const VISITED_BEFORE_KEY = 'roomtreatment.visited_before';

/** Has this browser been here before THIS session started?
 *
 *  Writes the marker as a side effect, so the answer is only ever true from
 *  the second visit onward. `null` means storage is unreachable, which the
 *  caller treats as "cannot tell, do not prompt" — the same conservative
 *  reading the session check uses. */
function wasReturningVisitor(): boolean | null {
  try {
    const seen = localStorage.getItem(VISITED_BEFORE_KEY) === '1';
    if (!seen) localStorage.setItem(VISITED_BEFORE_KEY, '1');
    return seen;
  } catch {
    return null;
  }
}

export default function SessionPricingGate() {
  const { open, isOpen } = useUpgradeModal();
  // Skip the gate when compute is already unlimited (legacy sub, lifetime,
  // founder, or unlimited grant). Anonymous visitors always see the prompt.
  const { authenticated, resolvedEntitlements, loading } = useWallet();
  const walletUnlimited = authenticated && hasUnlimitedCompute(resolvedEntitlements);
  const { isNative, capabilities } = useNativeHost();

  /** This gate opened the modal itself — distinguishes "open because of us"
   *  from "open because another surface opened it" in the watcher below. */
  const firedRef = useRef(false);
  /** Another pricing surface has owned the modal in this session; the gate
   *  stays down for good. */
  const standDownRef = useRef(false);
  /** Answered once, on the first effect run, because `wasReturningVisitor`
   *  WRITES the marker: asking it twice would report the visitor as
   *  returning on the second run of the same visit. */
  const returningRef = useRef<boolean | null | undefined>(undefined);

  // No two pricing surfaces back to back. The provider serialises every open
  // through one `isOpen` flag, which means a second open() while another
  // surface is showing silently replaces its trigger, prelude and secondary
  // action on screen — and firing the moment that one closes is the same
  // annoyance one frame later. So if the modal is open and it isn't ours (the
  // post-onboarding pre-upsell, the credit wall's 402 handler, a header
  // Upgrade click), this session is spent: mark it shown so a later route
  // change doesn't revive it either. Declared BEFORE the opening effect so it
  // wins on a commit where both would run.
  useEffect(() => {
    if (!isOpen || firedRef.current || standDownRef.current) return;
    standDownRef.current = true;
    markSessionShown();
  }, [isOpen]);

  useEffect(() => {
    if (standDownRef.current || firedRef.current) return;
    // Wait for the wallet: before it resolves, "no credits info" and "an
    // unlimited plan" look identical. Ask BEFORE the visit marker is
    // touched, or a visitor whose wallet is still loading is recorded as
    // having visited and is then prompted on the very next effect run.
    if (loading || walletUnlimited) return;
    // Record the visit FIRST, before either exclusion can return early. An
    // ad visitor has genuinely seen the product: when they come back
    // organically next week they are a returning visitor, which is precisely
    // who this gate is for. Checking the ad angle first would skip the write
    // and treat that return as a first visit, losing the one pitch that
    // lands.
    if (returningRef.current === undefined) {
      returningRef.current = wasReturningVisitor();
    }
    // Never on a session that arrived through a paid ad. The click is bought
    // and the visitor has seen nothing yet; the post-onboarding paywall is
    // where that session gets asked.
    if (storedCampaignAngle()) return;
    // Unreachable storage reads as "cannot tell", and a first-ever visit is
    // remembered rather than pitched at.
    if (returningRef.current !== true) return;
    // App Store guideline 3.1.1 / ADR 009: inside the app a plan is sold only
    // where the storefront allows an external purchase link, which the shell
    // announces as `capabilities.checkout === 'web'`. This is the same gate
    // UpgradeModalProvider.open() applies — repeated here so the gate doesn't
    // burn its once-per-session flag on an open that would be a no-op. The
    // component paints nothing, so there is no `data-commerce` element to
    // mark; this check is its equivalent.
    if (isNative && capabilities.checkout !== 'web') return;
    // The internal (noindex) design-exploration routes drive this modal
    // themselves to preview specific sequences (design/a|b|c's DirectionA
    // etc.) — this prompt popping over that mid-review would just be noise.
    if (window.location.pathname.startsWith('/design')) return;
    // Nor over a checkout's own result: /checkout/complete is where a buyer
    // looks for "did that work?", and a plans screen stacked on it hid the
    // answer completely (app audit, 2026-09-27).
    if (window.location.pathname.startsWith('/checkout')) return;
    // Nor on a content page (tools, guides, glossary, room-mode sizes...): a
    // visitor there came from search for an answer, and a plans screen over
    // it costs the page its ranking signals (S6). Checked before the session
    // flag is read or written, so a skipped content page consumes nothing.
    // PENDING OWNER CONFIRMATION: ADR 012 decision 9. Revert this line if the
    // S20 funnel shows organic signups don't offset the lost opens.
    if (isContentPath(window.location.pathname)) return;
    const shown = readSessionShown();
    if (shown !== false) return;
    if (!markSessionShown()) return;
    firedRef.current = true;
    // No `dismissible` option exists any more (removed in bab8fa74): the
    // plans screen is ALWAYS closable — X, Escape, backdrop — on every way
    // in. See gotchas.md #12; the pre-upsell is the one exception, and this
    // gate opens straight onto plans with no prelude.
    open({ trigger: 'session_start' });
  }, [loading, walletUnlimited, isNative, capabilities.checkout, open]);

  return null;
}
